// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import { ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { getDataViewKey } from "@insite/client-framework/Store/Data/DataState";
import setAllWishListLinesIsSelected, {
    SetAllWishListLinesIsSelectedParameter,
} from "@insite/client-framework/Store/Pages/MyListDetails/Handlers/SetAllWishListLinesIsSelected";
import { WishListLineModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import GridItem from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Checkbox, { CheckboxPresentationProps } from "@store/MobiusOverrides/Checkbox/Checkbox";
import Link, { LinkPresentationProps } from "@store/MobiusOverrides/Link/Link";
import MyListsDetailsBunzlProductListLine from "@store/Library/Widgets/MyListDetails/MyListsDetailsBunzlProductListLine";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { toToolkitApplicationState } from "@toolkit/Framework/Store/ApplicationState";
import React, { FC } from "react";
import { useDispatch } from "react-redux";
import { css } from "styled-components";

interface MyListDetailsCategoryViewProps {
    productInfosByWishListLineId: SafeDictionary<ProductInfo>;
    wishList: WishListModel;
    onDeleteClick: (wishListLine: WishListLineModel) => void;
    onEditNotesClick: (wishListLine: WishListLineModel) => void;
    maximumNumberOfImageBadges: number;
    maximumNumberOfTextBadges: number;
    showImageBadges: boolean;
    showTextBadges: boolean;
    wrapperStyles?: InjectableCss;
    gridContainerStyles?: InjectableCss;
    extendedStyles?: CategoryViewStyles;
    overrideStyles?: CategoryViewStyles;
}

interface CategoryViewStyles {
    headerSectionWrapper: InjectableCss;
    checkboxStyles?: CheckboxPresentationProps;
    categoryLink?: LinkPresentationProps;
}

const categoryViewStyles: CategoryViewStyles = {
    headerSectionWrapper: {
        css: css`
            display: flex;
            padding-block: ${pxToRem(20)};
            gap: ${pxToRem(12)};
        `,
    },
    categoryLink: {
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            css: css`
                font-size: ${pxToRem(16)};
                display: flex;
                padding-top: ${pxToRem(1)};
            `,
        },
    },
};

const MyListDetailsCategoryView: FC<MyListDetailsCategoryViewProps> = ({
    productInfosByWishListLineId,
    wishList,
    onDeleteClick,
    onEditNotesClick,
    maximumNumberOfImageBadges,
    maximumNumberOfTextBadges,
    showImageBadges,
    showTextBadges,
    wrapperStyles,
    gridContainerStyles,
    extendedStyles,
    overrideStyles,
}) => {
    const dispatch = useDispatch();
    const styles = useExpandProps(categoryViewStyles, extendedStyles, overrideStyles);
    const selectedWishListLines = useAppSelector(
        state => toToolkitApplicationState(state).pages.myListDetails.selectedWishListLineIds,
    );
    const categoryDataView = useAppSelector(
        state =>
            toToolkitApplicationState(state).bunzl.pages.myListDetailsCategory.dataViews[
                getDataViewKey(state.pages.myListDetails.loadWishListLinesParameter)
            ],
    );
    const categoriesById = useAppSelector(
        state => toToolkitApplicationState(state).bunzl.pages.myListDetailsCategory.byId,
    );

    const selectAllChangeHandler = (newValue: boolean, products: WishListLineModel[]) => {
        const parameter: SetAllWishListLinesIsSelectedParameter = {
            wishListLineIds: products.map(product => product.id),
            isSelected: newValue,
        };
        dispatch(setAllWishListLinesIsSelected(parameter));
    };
    if (!categoryDataView) {
        return null;
    }

    return (
        <StyledWrapper {...wrapperStyles} data-test-selector="myListDetails_Category_lines">
            {categoryDataView?.ids?.map(id => {
                const isOtherCategory = id === emptyGuid;
                const category = categoriesById[id];

                const productInfoLoaded = category.wishListProducts.every(
                    product => productInfosByWishListLineId[product.id] !== undefined,
                );
                if (!productInfoLoaded || !category?.wishListProducts.length) {
                    return null;
                }
                const isChecked = category.wishListProducts.every(product =>
                    selectedWishListLines.includes(product.id),
                );
                return (
                    <React.Fragment key={category.id}>
                        <StyledWrapper {...styles.headerSectionWrapper}>
                            <Checkbox
                                checked={isChecked}
                                sizeVariant="small"
                                onChange={(_, value) => selectAllChangeHandler(value, category.wishListProducts)}
                                data-test-selector="selectAllItems"
                            />

                            <Link {...styles.categoryLink} href={isOtherCategory ? undefined : category.path}>
                                {category.shortDescription.toUpperCase()}
                            </Link>
                        </StyledWrapper>
                        <>
                            {category.wishListProducts.map(product => {
                                const productInfo = productInfosByWishListLineId[product.id];
                                if (!productInfo) {
                                    return null;
                                }

                                return (
                                    <GridItem {...gridContainerStyles} key={product.id}>
                                        <MyListsDetailsBunzlProductListLine
                                            canDrag={false}
                                            isEditingSortOrder={false}
                                            wishList={wishList}
                                            wishListLine={product}
                                            showSpecificationModal
                                            productInfo={productInfo}
                                            onDeleteClick={onDeleteClick}
                                            onEditNotesClick={onEditNotesClick}
                                            showImageBadges={showImageBadges}
                                            showTextBadges={showTextBadges}
                                            maximumNumberOfImageBadges={maximumNumberOfImageBadges}
                                            maximumNumberOfTextBadges={maximumNumberOfTextBadges}
                                        />
                                    </GridItem>
                                );
                            })}
                        </>
                    </React.Fragment>
                );
            })}
        </StyledWrapper>
    );
};

export default MyListDetailsCategoryView;
